import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../components/MyModal'
import MyInput from '../components/MyInput'
import MyTextArea from './MyTextArea'
import MyButton from '../components/buttons'
import { FaUser, FaEnvelope } from 'react-icons/fa'
import { contactService } from '../../services/contactService'

interface ReportUserModalProps {
  isOpen: boolean
  onClose: () => void
  userNickname?: string | null
  userId?: string | number
}

const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  userId = '',
}) => {
  const { t } = useTranslation()
  const [submissionStatus, setSubmissionStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [formError, setFormError] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)

  const [lastSubmitTime, setLastSubmitTime] = useState<number>(() => {
    const saved = localStorage.getItem('lastReportSubmitTime')
    return saved ? parseInt(saved) : 0
  })
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [canSubmit, setCanSubmit] = useState<boolean>(true)

  const [formData, setFormData] = useState({
    firstname: '',
    title: `(reported-user : ${userId})`,
    description: '',
    email: '',
  })

  useEffect(() => {
    const checkCooldown = () => {
      const now = Date.now()
      const timePassed = now - lastSubmitTime
      const cooldownPeriod = 5 * 60 * 1000 // 5 minutes

      if (timePassed < cooldownPeriod) {
        setCanSubmit(false)
        setTimeRemaining(Math.ceil((cooldownPeriod - timePassed) / 1000))
      } else {
        setCanSubmit(true)
        setTimeRemaining(0)
      }
    }

    const timer = setInterval(checkCooldown, 1000)
    checkCooldown()

    return () => clearInterval(timer)
  }, [lastSubmitTime])

  useEffect(() => {
    const isValid =
      formData.firstname.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.description.trim() !== ''
    setIsFormValid(isValid)
  }, [formData])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!canSubmit) {
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
      return
    }

    if (!formData.description.trim()) {
      setFormError(t('description-required'))
      return
    }

    try {
      await contactService.sendContactForm(formData)

      const now = Date.now()
      setLastSubmitTime(now)
      localStorage.setItem('lastReportSubmitTime', now.toString())

      setSubmissionStatus('success')
      setIsStatusModalOpen(true)
      onClose()
    } catch (error) {
      console.error('Error sending form:', error)
      setSubmissionStatus('error')
      setIsStatusModalOpen(true)
    }
  }

  const handleChange = (field: string) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
    setFormError('')
  }

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false)
    if (submissionStatus === 'success') {
      onClose()
      setFormData({
        ...formData,
        firstname: '',
        description: '',
        email: '',
      })
      setFormError('')
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('report-user')}
          </h2>
          <form onSubmit={handleSubmit} className='p-5 space-y-5'>
            <div className='space-y-6'>
              <MyInput
                label={t('name-firstname-nickname')}
                value={formData.firstname}
                onChange={handleChange('firstname')}
                className='text-anthracite'
                icon={<FaUser />}
                required
                autoComplete='name'
              />

              <MyInput
                label={t('email')}
                value={formData.email}
                onChange={handleChange('email')}
                type='email'
                className='text-anthracite'
                icon={<FaEnvelope />}
                required
                autoComplete='email'
              />

              <div className='space-y-1'>
                <MyTextArea
                  label={t('message')}
                  multiline
                  variant='grisclair'
                  placeholder={t('reason')}
                  showEmoji={false}
                  name='message'
                  value={formData.description}
                  onChange={handleChange('description')}
                  required
                  className='text-sm'
                  InputProps={{
                    style: {
                      height: '160px',
                      alignItems: 'flex-start',
                    },
                  }}
                />
                {formError && (
                  <p className='text-redpink text-sm'>{formError}</p>
                )}
              </div>
            </div>

            <div className='flex justify-end space-x-2'>
              <MyButton
                variant='anthracite-redpink'
                onClick={onClose}
                className='px-4 py-2'
              >
                {t('cancel')}
              </MyButton>
              <MyButton
                variant={
                  isFormValid && canSubmit ? 'redpink' : 'redpink-inactive'
                }
                type='submit'
                className='px-4 py-2'
                disabled={!isFormValid || !canSubmit}
              >
                {!canSubmit
                  ? `${t('send')} (${Math.floor(timeRemaining / 60)}:${(
                      timeRemaining % 60
                    )
                      .toString()
                      .padStart(2, '0')})`
                  : t('send')}
              </MyButton>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={isStatusModalOpen} onClose={handleCloseStatusModal}>
        <div className='bg-white rounded-lg'>
          <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
            {t('submission-status')}
          </h2>
          <div className='space-y-4 p-5'>
            <p
              className={`text-center rounded-lg p-2 mb-4 ${
                submissionStatus === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-redlight text-redpink'
              }`}
            >
              {submissionStatus === 'success'
                ? t('report-form-success')
                : t('report-form-error')}
            </p>
            <div className='flex justify-end'>
              <button
                onClick={handleCloseStatusModal}
                className='bg-anthracite text-white px-4 py-2 rounded hover:bg-opacity-90'
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ReportUserModal
